import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CreditCard } from 'lucide-react';
import { StoreInterface } from '../../../store/types';
import useSpreedlyScript from '../../../hooks/payment/useSpreedlyScript';
import useBillingInfo from '../../../hooks/payment/useBillingInfo';
import useTheme from '../../../hooks/ui/useTheme';
import { ITheme } from '../../../providers/ThemeProvider';
import {
  SpreedlyField,
  SpreedlyFieldError,
  SpreedlyFieldEventType,
  SpreedlyIframeListeners,
  SpreedlyInputProperties,
  attachCardForm,
  detachCardForm,
} from '../../../services/helpers/SpreedlyIframe';
import { getCardBrandIcon } from '../payment-methods/PaymentMethodBrandIcons';
import {
  SpreedlyErrorField,
  SpreedlyErrorKey,
  describeSpreedlyErrors,
  fieldForErrorKey,
  mapSpreedlyErrors,
} from './spreedlyErrors';
import {
  brandForCardType,
  cvvBlurError,
  expectedCvvLength,
  isNumberComplete,
  numberBlurError,
  shouldAutoAdvanceFromNumber,
} from './cardBrands';
import { EXPIRY_MAX_LENGTH, formatExpiryInput, isExpiryComplete, parseExpiry } from './expiry';
import { FieldAppearance, fieldAppearance, fieldStyle, iframeInputCss, iframePlaceholderCss } from './stripeAppearance';

export interface SpreedlyCardFormHandle {
  tokenize: () => Promise<string>;
}

const NUMBER_EL_ID = 'spreedly-number-iframe';
const CVV_EL_ID = 'spreedly-cvv-iframe';
// The two plain inputs, so their labels can point at them; the iFrame fields get `setLabel`.
const EXPIRY_INPUT_ID = 'spreedly-expiry';
const NAME_INPUT_ID = 'spreedly-cardholder-name';

// Spreedly blocks external fonts inside its iFrames (CORS), so DM Sans cannot follow the digits in
// there. The Stripe Payment Element's default theme uses this same system stack, so the whole card
// form adopts it: the hosted digits and our own inputs then match each other and the reference.
const FIELD_FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
// Keeps the last digits clear of the brand icon that sits inside the number box: the slot's own
// width (BRAND_SLOT_WIDTH_PX) plus the 12px it is inset from the box's right edge, plus 12px of
// breathing room between the digits and the mark. The marks differ in aspect ratio — Visa is 2.8:1,
// so at h-4 it is 44px wide where Mastercard is 26px — hence a fixed slot the widest mark fits in
// (`max-w-full` on the icon) rather than a gutter sized for an average one.
const BRAND_SLOT_WIDTH_PX = 44;
const NUMBER_ICON_GUTTER_PX = BRAND_SLOT_WIDTH_PX + 24;

type FieldName = SpreedlyField | 'expiry' | 'name';

interface FieldState {
  focused: boolean;
}

type FieldStates = Record<FieldName, FieldState>;
type FieldErrors = Partial<Record<SpreedlyErrorField, SpreedlyErrorKey>>;

const INITIAL_FIELD_STATES: FieldStates = {
  number: { focused: false },
  expiry: { focused: false },
  cvv: { focused: false },
  name: { focused: false },
};

const EMPTY_INPUT_PROPERTIES: SpreedlyInputProperties = {
  cardType: '',
  validNumber: false,
  validCvv: false,
  numberLength: 0,
  cvvLength: 0,
  iin: '',
};

/**
 * Each iFrame reports on `input`; the documented payload is one state map for both fields, but
 * only the number frame can know the brand and the number length for sure. Take from each event
 * only what its own field owns, so a CVV keystroke can never blank the brand or reset the number
 * state the customer already typed.
 */
function mergeInputProperties(
  previous: SpreedlyInputProperties,
  field: SpreedlyField,
  reported: SpreedlyInputProperties,
): SpreedlyInputProperties {
  if (field === 'number') {
    return {
      ...previous,
      cardType: reported.cardType ?? '',
      iin: reported.iin ?? '',
      validNumber: reported.validNumber,
      numberLength: reported.numberLength,
    };
  }

  return { ...previous, validCvv: reported.validCvv, cvvLength: reported.cvvLength };
}

/**
 * The iframe inputs inherit nothing from the page — unstyled they render browser-default (tiny,
 * top-left) inside our boxes. Runs on EVERY `ready`: `reload()` resets the iFrames, so the chrome
 * (placeholders included) applied to the previous mount is gone.
 *
 * @see https://developer.spreedly.com/docs/iframe-ui
 */
function applyFieldChrome(spreedly: Spreedly.Sdk, t: TFunction, theme: ITheme) {
  // `tel` keeps the pretty spacing (which `number` would strip) and brings up the numeric keypad
  // on phones, the same keyboard our expiry input asks for.
  spreedly.setFieldType('number', 'tel');
  spreedly.setFieldType('cvv', 'tel');
  spreedly.setNumberFormat('prettyFormat');
  applyFieldColors(spreedly, theme);
  applyFieldCopy(spreedly, t);
}

/** Everything theme-dependent, so a light/dark switch can re-apply it without a `reload()`. */
function applyFieldColors(spreedly: Spreedly.Sdk, theme: ITheme) {
  const appearance = fieldAppearance(theme);
  const base = iframeInputCss(appearance, FIELD_FONT_FAMILY);

  spreedly.setStyle('number', `${base} padding-right: ${NUMBER_ICON_GUTTER_PX}px;`);
  spreedly.setStyle('cvv', base);
  spreedly.setStyle('placeholder', iframePlaceholderCss(appearance, FIELD_FONT_FAMILY));
}

/**
 * Only the translated strings — placeholders plus the screen-reader label/title of each iFrame
 * field. Re-run on a language switch; the rest of `applyFieldChrome` is language-independent.
 */
function applyFieldCopy(spreedly: Spreedly.Sdk, t: TFunction) {
  spreedly.setPlaceholder('number', t('card_number_placeholder'));
  spreedly.setPlaceholder('cvv', t('cvv_placeholder'));
  spreedly.setLabel('number', t('card_number'));
  spreedly.setTitle('number', t('card_number'));
  spreedly.setLabel('cvv', t('cvv_label'));
  spreedly.setTitle('cvv', t('cvv_label'));
}

const SpreedlyCardForm = forwardRef<SpreedlyCardFormHandle>(function SpreedlyCardForm(_props, ref) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Spreedly:SpreedlyCardForm' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { loaded, failed } = useSpreedlyScript();
  const theme = useTheme();
  const { isDarkMode } = theme;
  const { billingFullName } = useBillingInfo();

  // Prefilled the way the Stripe Payment Element prefills its "Full name" from billing details.
  // The name is required (decided 2026-09-15: Spreedly's `allow_blank_name` would trade an
  // unquantified acquirer/AVS decline risk for a small parity gain), so a cleared field is flagged
  // on blur and again at tokenization, before Spreedly is asked.
  const [name, setName] = useState(() => billingFullName ?? '');
  const [expiry, setExpiry] = useState('');
  const [ready, setReady] = useState(false);
  const [cardType, setCardType] = useState('');
  const [fields, setFields] = useState<FieldStates>(INITIAL_FIELD_STATES);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formErrors, setFormErrors] = useState<SpreedlyErrorKey[]>([]);

  const expiryInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const tokenizeResolveRef = useRef<((token: string) => void) | null>(null);
  const tokenizeRejectRef = useRef<((err: Error) => void) | null>(null);

  // The Spreedly handlers are registered once (their effect keys off the SDK, not the render), so
  // they read the current language, theme and hosted-field state through refs rather than the
  // values their closures captured.
  const appliedLanguageRef = useRef<string | null>(null);
  const appliedDarkModeRef = useRef<boolean | null>(null);
  const languageRef = useRef(i18n.language);
  const tRef = useRef(t);
  const themeRef = useRef(theme);
  // The latest state Spreedly reported for its two fields, merged per field — what blur
  // validation reads. Null until the customer has typed into either iFrame.
  const inputPropertiesRef = useRef<SpreedlyInputProperties | null>(null);
  const numberCompleteRef = useRef(false);
  // The field the form is sending the cursor to BECAUSE that field has an error. Focus normally
  // clears a field's message (the customer has come back to fix it), which would otherwise erase
  // the very message we are sending them to read.
  //
  // A field name rather than a boolean with a timer: the focus a `transferFocus` produces comes
  // back from Spreedly's own document — parent postMessage → its frame → `focus()` → postMessage
  // back — so there is no interval this side of the wire that reliably spans it, and a timer short
  // enough not to swallow an unrelated focus is one that usually expires before the echo lands.
  // The token is consumed by the NEXT focus of any field instead: the echo it was set for keeps its
  // message, a focus of some other field clears that field's own message as usual, and a token for
  // an echo that never arrives is spent harmlessly by whatever the customer focuses next.
  const keepErrorOnFocusRef = useRef<FieldName | null>(null);

  /** True when this focus is the one the form asked for; consumes the token either way. */
  const shouldKeepErrorOnFocus = (field: FieldName) => {
    const keep = keepErrorOnFocusRef.current === field;

    keepErrorOnFocusRef.current = null;

    return keep;
  };

  languageRef.current = i18n.language;
  tRef.current = t;
  themeRef.current = theme;

  // The key lives on the provider (the environment is per partner); the
  // company-level fallback covers older cached payloads.
  const environmentKey = company?.service_provider?.spreedly_environment_key ?? company?.spreedly_environment_key;

  const settleTokenize = (settle: (resolve: (token: string) => void, reject: (err: Error) => void) => void) => {
    const resolve = tokenizeResolveRef.current;
    const reject = tokenizeRejectRef.current;

    tokenizeResolveRef.current = null;
    tokenizeRejectRef.current = null;

    if (resolve && reject) {
      settle(resolve, reject);
    }
  };

  const patchField = (field: FieldName, patch: Partial<FieldState>) =>
    setFields((previous) => ({ ...previous, [field]: { ...previous[field], ...patch } }));

  const setFieldError = (field: SpreedlyErrorField, key: SpreedlyErrorKey | null) =>
    setFieldErrors((previous) => {
      if (key) {
        return { ...previous, [field]: key };
      }

      if (!(field in previous)) {
        return previous;
      }

      const { [field]: _cleared, ...rest } = previous;

      return rest;
    });

  /** Inline validity for the two hosted fields, from Spreedly's last `input` state map. */
  const validateHostedFieldOnBlur = (field: SpreedlyField) => {
    const properties = inputPropertiesRef.current;

    if (!properties) {
      return;
    }

    setFieldError(field, field === 'number' ? numberBlurError(properties) : cvvBlurError(properties));
  };

  const onHostedFieldEvent = (
    field: SpreedlyField,
    type: SpreedlyFieldEventType,
    _activeEl: SpreedlyField,
    properties?: SpreedlyInputProperties,
  ) => {
    if (type === 'focus') {
      patchField(field, { focused: true });

      // Same rule as the plain inputs: a focus the form caused in order to show an error must not
      // clear it. This is the path the `transferFocus` echo arrives on.
      if (!shouldKeepErrorOnFocus(field)) {
        setFieldError(field, null);
      }

      return;
    }

    if (type === 'blur') {
      patchField(field, { focused: false });
      // Leaving the field ends any claim a pending token had on it; blur re-validates from
      // Spreedly's own state, which is the authority from here on.
      keepErrorOnFocusRef.current = null;
      validateHostedFieldOnBlur(field);

      return;
    }

    if (type !== 'input' || !properties) {
      return;
    }

    const merged = mergeInputProperties(inputPropertiesRef.current ?? EMPTY_INPUT_PROPERTIES, field, properties);

    inputPropertiesRef.current = merged;

    if (field === 'cvv') {
      return;
    }

    setCardType(merged.cardType);

    const complete = isNumberComplete(merged);
    const justCompleted = complete && !numberCompleteRef.current;

    numberCompleteRef.current = complete;

    // Stripe/Adyen move on the moment the number is whole — at the brand's one common length, so
    // neither a still-typing Maestro customer nor a 13-digit checksum fluke on a Visa yanks the
    // cursor out of the field.
    if (justCompleted && shouldAutoAdvanceFromNumber(merged)) {
      expiryInputRef.current?.focus();
    }
  };

  const resetHostedFieldState = () => {
    inputPropertiesRef.current = null;
    numberCompleteRef.current = false;
    setCardType('');
    setFields(INITIAL_FIELD_STATES);
  };

  const FIELD_TARGETS: Record<SpreedlyErrorField, string> = {
    number: `[data-test="spreedly-number-box"]`,
    expiry: `#${EXPIRY_INPUT_ID}`,
    cvv: `[data-test="spreedly-cvv-box"]`,
    name: `#${NAME_INPUT_ID}`,
  };

  /**
   * Puts the customer on the field that stopped the payment. The Pay button sits in the summary
   * column, which on a phone is a screen away from the form — without this, a tap that fails
   * looks like a button that does nothing, because the message it rendered is off-screen.
   *
   * Declared ABOVE the handler-registration effect on purpose: that effect runs on mount, before a
   * `const` further down this body is initialized, and `onErrors` closes over this name. The
   * register-once pattern keeps the first mount's listeners alive, so a dead-zone reference would
   * throw for the life of the page, not just once.
   *
   * Field order is the visual one. The two hosted inputs live in Spreedly's documents, so only the
   * SDK can focus them; scrolling our own box brings the right one into view either way. The errors
   * are passed in rather than read from state, which has not re-rendered yet.
   */
  const focusFirstInvalidField = (errors: FieldErrors) => {
    const target = (['number', 'expiry', 'cvv', 'name'] as const).find((field) => errors[field]);

    if (!target) {
      return;
    }

    document.querySelector(FIELD_TARGETS[target])?.scrollIntoView({ block: 'center', behavior: 'smooth' });

    // Claimed for this field before the focus is asked for, and consumed by that focus whenever it
    // arrives — synchronously for our own inputs, one cross-document round trip later for Spreedly's.
    keepErrorOnFocusRef.current = target;

    if (target === 'number' || target === 'cvv') {
      window.Spreedly?.transferFocus?.(target);

      return;
    }

    (target === 'expiry' ? expiryInputRef : nameInputRef).current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!loaded || !environmentKey) return;

    const spreedly = window.Spreedly;

    const listeners: SpreedlyIframeListeners = {
      onReady: () => {
        applyFieldChrome(spreedly, tRef.current, themeRef.current);
        appliedLanguageRef.current = languageRef.current;
        appliedDarkModeRef.current = themeRef.current.isDarkMode;
        // `reload()` hands us empty iFrames; whatever brand/completion the previous ones reported
        // no longer describes anything.
        resetHostedFieldState();
        setReady(true);
      },
      onPaymentMethod: (token: string) => {
        settleTokenize((resolve) => resolve(token));
      },
      onErrors: (errors: SpreedlyFieldError[]) => {
        // The customer reads the mapped copy under the field it concerns; the raw developer
        // messages travel only in the rejected Error, which the pay button logs.
        const byField: FieldErrors = {};
        const formLevel: SpreedlyErrorKey[] = [];

        mapSpreedlyErrors(errors).forEach((key) => {
          const field = fieldForErrorKey(key);

          if (!field) {
            formLevel.push(key);

            return;
          }

          byField[field] ??= key;
        });

        setFieldErrors(byField);
        setFormErrors(formLevel);
        // Same reason as the local rejections: the customer pressed Pay from the summary column and
        // the message that explains the refusal is up in the form.
        focusFirstInvalidField(byField);
        settleTokenize((_resolve, reject) => reject(new Error(describeSpreedlyErrors(errors))));
      },
      onFieldEvent: onHostedFieldEvent,
    };

    attachCardForm(spreedly, environmentKey, { numberEl: NUMBER_EL_ID, cvvEl: CVV_EL_ID }, listeners);

    return () => {
      detachCardForm(listeners);
      // A tokenize awaiting this instance can never settle through Spreedly now — its events
      // route to the next form (or nowhere). Reject so the pay button releases instead of hanging.
      settleTokenize((_resolve, reject) => reject(new Error('card_form_unmounted')));
    };
  }, [loaded, environmentKey]);

  // The storefront's language picker sits on the same screen as checkout, so a switch must reach
  // the iFrame copy too — it lives inside Spreedly's own documents and no re-render touches it.
  // Gated on `ready`: the SDK has nothing to place into before the iFrames exist. `ready` itself
  // already applied the current language (via applyFieldChrome), so only an actual change
  // re-applies — otherwise every mount would place the same copy twice.
  useEffect(() => {
    if (!ready) return;

    if (appliedLanguageRef.current === i18n.language) return;

    appliedLanguageRef.current = i18n.language;

    applyFieldCopy(window.Spreedly, t);
  }, [i18n.language, ready]);

  // Same shape for the theme: the dark-mode toggle re-colours our inputs through Tailwind, but the
  // iFrame inputs only ever see what we push in. Restyling beats `reload()`, which would also
  // wipe whatever the customer has typed.
  useEffect(() => {
    if (!ready) return;

    if (appliedDarkModeRef.current === isDarkMode) return;

    appliedDarkModeRef.current = isDarkMode;

    applyFieldColors(window.Spreedly, theme);
  }, [isDarkMode, ready]);

  const onExpiryChange = (raw: string) => {
    const next = formatExpiryInput(raw, expiry);

    setExpiry(next);

    if (!isExpiryComplete(next)) {
      setFieldError('expiry', null);

      return;
    }

    // A whole date is judged at once, whether it was just completed or edited in place —
    // replacing a valid year with a past one must not wait for blur.
    const parsed = parseExpiry(next);

    setFieldError('expiry', parsed.error);

    if (parsed.error || isExpiryComplete(expiry)) {
      return;
    }

    // Just completed and valid. The CVV lives in Spreedly's document; only the SDK can put the
    // cursor there.
    window.Spreedly?.transferFocus?.('cvv');
  };

  const onExpiryBlur = () => {
    patchField('expiry', { focused: false });

    if (expiry.length === 0) {
      return;
    }

    setFieldError('expiry', parseExpiry(expiry).error);
  };

  const onNameBlur = () => {
    patchField('name', { focused: false });
    setFieldError('name', name.trim() ? null : 'name_blank');
  };

  useImperativeHandle(ref, () => ({
    tokenize: (): Promise<string> => {
      const parsed = parseExpiry(expiry);
      const trimmedName = name.trim();

      // Our two plain fields are checked here, before the round trip. Spreedly would report the
      // same problems, but only after a tokenization attempt; the hosted fields it must judge itself.
      const localErrors: FieldErrors = {
        ...(parsed.error ? { expiry: parsed.error } : {}),
        ...(trimmedName ? {} : { name: 'name_blank' as const }),
      };

      setFormErrors([]);
      // The number/cvv messages already on screen came from Spreedly's own field state on blur and
      // are still true. Dropping them here and letting the `errors` event put them back made them
      // blink; keep them and let the next blur or `errors` payload replace them.
      setFieldErrors((previous) => ({
        ...(previous.number ? { number: previous.number } : {}),
        ...(previous.cvv ? { cvv: previous.cvv } : {}),
        ...localErrors,
      }));

      if (parsed.error || !trimmedName) {
        focusFirstInvalidField(localErrors);

        return Promise.reject(new Error(parsed.error ? 'invalid_expiry' : 'invalid_name'));
      }

      return new Promise((resolve, reject) => {
        // Overwriting the refs would leave an earlier tokenize waiting forever — Spreedly's answer
        // can only ever settle the newest call. Reject the superseded one explicitly.
        settleTokenize((_resolve, rejectPrevious) => rejectPrevious(new Error('tokenize_superseded')));

        tokenizeResolveRef.current = resolve;
        tokenizeRejectRef.current = reject;

        window.Spreedly.tokenizeCreditCard({
          month: parsed.month,
          year: parsed.year,
          full_name: trimmedName,
        });
      });
    },
  }));

  const brandIcon = getCardBrandIcon(brandForCardType(cardType));
  const appearance = fieldAppearance(theme);

  const errorId = (field: SpreedlyErrorField) => `spreedly-${field}-error`;

  const renderFieldError = (field: SpreedlyErrorField) => {
    const key = fieldErrors[field];

    if (!key) {
      return null;
    }

    return (
      // `role="alert"` announces the message the moment it appears — the only route for the two
      // hosted fields, whose inputs live in Spreedly's documents where no aria of ours can reach.
      <p
        id={errorId(field)}
        role="alert"
        className={errorText}
        style={{ color: appearance.danger }}
        data-test="spreedly-field-error"
        data-field={field}
      >
        {t(`errors.${key}`, { count: expectedCvvLength(cardType) })}
      </p>
    );
  };

  /** The plain inputs can point a screen reader at their own message. */
  const ariaFor = (field: SpreedlyErrorField) => ({
    'aria-invalid': Boolean(fieldErrors[field]),
    'aria-describedby': fieldErrors[field] ? errorId(field) : undefined,
  });

  // Invalid keeps the danger border under the focus ring, as Stripe's `.Input--invalid:focus`
  // does (see fieldStyle). Focus itself clears our own blur errors, so the combination shows only
  // for an error raised while typing — a complete but expired date.
  const styleFor = (field: FieldName) =>
    fieldStyle(appearance, {
      focused: fields[field].focused,
      invalid: Boolean(fieldErrors[field as SpreedlyErrorField]),
    });

  /** Clicking a hosted field's label should land the cursor in it, as a real `htmlFor` would. */
  const focusHostedField = (field: SpreedlyField) => window.Spreedly?.transferFocus?.(field);

  const focusHandlers = (field: FieldName) => ({
    onFocus: () => {
      patchField(field, { focused: true });

      // Coming back to fix a field clears its message; being SENT here by a failed Pay keeps it,
      // since the message is the reason the cursor moved.
      if (shouldKeepErrorOnFocus(field)) {
        return;
      }

      setFieldError(field as SpreedlyErrorField, null);
    },
    onBlur: () => patchField(field, { focused: false }),
  });

  return (
    <div className={container} style={{ fontFamily: FIELD_FONT_FAMILY }}>
      {/* Stripe's row: number wide, expiry and security code narrow; stacked 2+1+1 on phones. */}
      <div className={cardRow}>
        <div className={numberCell}>
          {/* No `htmlFor`: the input is in Spreedly's document, so a label cannot point at it and
              the browser's own click-to-focus never happens. `transferFocus` is the only way in,
              which is what makes these two labels behave like the plain inputs' labels. The
              screen-reader label inside the iFrame comes from `setLabel` (see applyFieldCopy). */}
          <label className={hostedLabel} style={{ color: appearance.text }} onClick={() => focusHostedField('number')}>
            {t('card_number')}
          </label>
          <div className={iframeBox} style={styleFor('number')} data-test="spreedly-number-box">
            <div id={NUMBER_EL_ID} className={iframeHost} />
            {/* Outside the iFrame but inside the box: the brand appears the moment the IIN resolves. */}
            <span
              className={brandSlot}
              // Inline, so it cannot drift from the iFrame's padding-right (both derive from
              // BRAND_SLOT_WIDTH_PX). Right-aligned inside the slot: a narrow mark sits where the
              // wide one ends, so the gap to the digits is the same for every brand.
              style={{ width: `${BRAND_SLOT_WIDTH_PX}px` }}
              aria-hidden="true"
              data-test="spreedly-card-brand"
              data-brand={cardType || 'unknown'}
            >
              {brandIcon ?? <CreditCard className={genericBrandIcon} style={{ color: appearance.placeholder }} />}
            </span>
          </div>
          {renderFieldError('number')}
        </div>

        <div>
          <label className={label} style={{ color: appearance.text }} htmlFor={EXPIRY_INPUT_ID}>
            {t('expiry')}
          </label>
          <input
            id={EXPIRY_INPUT_ID}
            ref={expiryInputRef}
            type="text"
            inputMode="numeric"
            value={expiry}
            onChange={(e) => onExpiryChange(e.target.value)}
            onFocus={focusHandlers('expiry').onFocus}
            onBlur={onExpiryBlur}
            placeholder={t('expiry_placeholder')}
            maxLength={EXPIRY_MAX_LENGTH}
            required
            {...ariaFor('expiry')}
            className={input}
            style={{ ...styleFor('expiry'), ...placeholderVariable(appearance) }}
            autoComplete="cc-exp"
            data-test="spreedly-expiry"
          />
          {renderFieldError('expiry')}
        </div>

        <div>
          <label className={hostedLabel} style={{ color: appearance.text }} onClick={() => focusHostedField('cvv')}>
            {t('cvv')}
          </label>
          <div className={iframeBox} style={styleFor('cvv')} data-test="spreedly-cvv-box">
            <div id={CVV_EL_ID} className={iframeHost} />
          </div>
          {renderFieldError('cvv')}
        </div>
      </div>

      <div>
        <label className={label} style={{ color: appearance.text }} htmlFor={NAME_INPUT_ID}>
          {t('cardholder_name')}
        </label>
        <input
          id={NAME_INPUT_ID}
          ref={nameInputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={focusHandlers('name').onFocus}
          onBlur={onNameBlur}
          placeholder={t('cardholder_name_placeholder')}
          required
          {...ariaFor('name')}
          className={input}
          style={{ ...styleFor('name'), ...placeholderVariable(appearance) }}
          autoComplete="cc-name"
          data-test="spreedly-cardholder-name"
        />
        {renderFieldError('name')}
      </div>

      {!ready && loaded && (
        <p className={hintText} style={{ color: appearance.placeholder }}>
          {t('loading')}
        </p>
      )}

      {failed && (
        <p className={errorText} style={{ color: appearance.danger }} data-test="spreedly-sdk-failed">
          {t('sdk_load_failed')}
        </p>
      )}

      {formErrors.length > 0 && (
        <div className={errorList} data-test="spreedly-card-errors">
          {formErrors.map((key) => (
            <p key={key} className={errorText} style={{ color: appearance.danger }}>
              {t(`errors.${key}`)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
});

export default SpreedlyCardForm;

/** The placeholder colour reaches `::placeholder` through a custom property the class below reads. */
const placeholderVariable = (appearance: FieldAppearance) =>
  ({ '--field-placeholder': appearance.placeholder }) as CSSProperties;

// No padding of its own: the payment-method row already pads its content, and the Stripe Payment
// Element in the same slot sits flush against that padding. Colours, borders, radius and the
// focus/invalid rings are inline (see stripeAppearance.ts); the classes below are layout only.
const container = 'space-y-sm';
// Stripe's `.Label`: 0.93rem, regular weight, a quarter-rem above the field.
const label = 'block mb-1 text-[0.93rem] font-normal';
// The two hosted fields' labels focus their iFrame through the SDK instead of `htmlFor`, so they
// need the pointer a real label has.
const hostedLabel = `${label} cursor-pointer`;
const cardRow = 'grid grid-cols-2 gap-sm md:grid-cols-[2fr_1fr_1fr]';
const numberCell = 'col-span-2 md:col-span-1';
// Literal 44px, not a template: Tailwind only generates classes it can read verbatim from source.
// Keep in step with FIELD_HEIGHT_PX in stripeAppearance.ts.
const input = 'w-full h-[44px] px-3 text-[16px] placeholder:text-[color:var(--field-placeholder)]';
// mp-block: these boxes host Spreedly's card-number and CVV iFrames. Session replay must never
// record them — the same guarantee the Stripe/Adyen payment iframes get in AnalyticsProvider. The
// box carries it as well as the host so the brand slot (which reveals the card type) is covered.
const iframeBox = 'mp-block relative w-full h-[44px] overflow-hidden';
// The <iframe> Spreedly injects gets the browser's default 2px inset border and, when focused, its
// focus-visible outline (a grey rounded frame inside our box). Strip both and make it fill the host.
const iframeHost =
  'mp-block h-full w-full [&_iframe]:block [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:border-0 [&_iframe]:outline-none';
// `justify-end` + the inline width give every brand the same right edge. `[&>svg]:max-w-full` caps
// a mark wider than the slot instead of letting it grow left over the digits — the icons set their
// own `h-4` and `getCardBrandIcon` passes no className, so the constraint has to come from here.
const brandSlot =
  'pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-end [&>svg]:max-w-full';
const genericBrandIcon = 'h-4 w-4';
const hintText = 'text-xs';
const errorList = 'space-y-1';
const errorText = 'mt-1 text-[0.93rem]';
